import { plannedMatchPlayerIds, retainPlannedSelection } from '../utils/matchSquad';
import { matchPositionSettings } from '../utils/matchPlayerPositions';
import { defaultPlanningOptions } from '../utils/planningOptions';
import { assertParticipationSaved } from '@waedo/frontend-core/player-participation';
/**
 * @file src/store/matchStore.ts
 * @fileoverview Dit bestand definieert de Zustand-store voor het beheren van wedstrijdgerelateerde state.
 * Het is verantwoordelijk voor:
 * - Het opslaan van een lijst met alle wedstrijden die relevant zijn voor de huidige gebruiker/team.
 * - Het beheren van de momenteel geselecteerde wedstrijd en de details ervan (opstelling, wissels, status, tijd).
 * - Het afhandelen van CRUD-operaties voor wedstrijden.
 * - Het beheren van wedstrijdconfiguratie-instellingen (bijv. kwartduur, wisselinterval).
 * - Het bieden van acties om de wedstrijdstatus bij te werken, zoals spelerstoewijzingen, wissels,
 *   wedstrijdtijd, status en huidig kwart.
 * - Het aanbieden van hulpprogrammafuncties met betrekking tot wedstrijden, zoals het ophalen van de volgende wisseltijd
 *   of spelerstatistieken voor een wedstrijd.
 * Alle persistente gegevens worden gesynchroniseerd met de backend.
 */
import { create } from 'zustand';
import { Match, Player, Position, Substitution, MatchConfig, PlayerStatistics, MatchTypeMetadata } from '../types';
import { createMatchApi, deleteMatchApi, getMatchApi, listMatchesApi, updateMatchApi } from '../lib/matchesApi';
import { listPlayersApi } from '../lib/playersApi';
import { getMatchTypeMetaApi } from '../lib/matchTypesApi';
import { getFormationApi } from '../lib/formationsApi';
import { getTeamApi } from '../lib/teamsApi';
import { mergeMatchPositionOverride, sanitizePlayerForSchedule, normalizePositionPreferences } from '../utils/player';
import { getPlayerPositionPriority } from '../utils/positions';
import { encryptValue, decryptIfEncrypted, looksEncrypted } from '../utils/encryption';
import { applyLiveSubstitution } from '../utils/liveSubstitution';
import { parseLastMatchDefaults } from '../utils/teamMatchSettings';

type MatchPlayerRecord = Record<string, unknown>;
type MatchApiRecord = Record<string, unknown>;
type MatchUpdatePayload = Omit<Partial<Match>, 'lineup' | 'reserve_players' | 'substitution_schedule' | 'substitutions'> & {
  formation_type?: 'positions' | 'lines';
  substitutions_per_quarter?: number | null;
  club_id?: string | null;
  lineup?: Record<string, string | MatchPlayerRecord | null> | null;
  reserve_players?: Array<string | MatchPlayerRecord>;
  substitution_schedule?: Record<string, unknown> | null;
  substitutions?: Array<Record<string, unknown>>;
};

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNullableString = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

const asNumber = (value: unknown, fallback = 0): number =>
  typeof value === 'number' ? value : fallback;

const asNullableNumber = (value: unknown): number | null =>
  typeof value === 'number' ? value : null;

const asNullableBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') return value;
  if (value === 1) return true;
  if (value === 0) return false;
  if (typeof value === 'string') {
    const normalized = value.trim().toLocaleLowerCase();
    if (['1', 'true', 'home', 'thuis'].includes(normalized)) return true;
    if (['0', 'false', 'away', 'uit'].includes(normalized)) return false;
  }
  return null;
};

const normalizePlayerId = (value: unknown): string =>
  String(value ?? '').trim();

const asRecordArray = (value: unknown): MatchApiRecord[] =>
  Array.isArray(value) ? value.filter((entry): entry is MatchApiRecord => !!entry && typeof entry === 'object') : [];

const maybeDecryptPlayerRefs = async (value: unknown, clubId: string): Promise<unknown> => {
  if (!clubId || value === null || value === undefined) return value;

  if (Array.isArray(value)) {
    return Promise.all(value.map((entry) => maybeDecryptPlayerRefs(entry, clubId)));
  }

  if (typeof value !== 'object') {
    return value;
  }

  const record = value as Record<string, unknown>;
  const nextRecord: Record<string, unknown> = { ...record };

  if (typeof record.name === 'string' && record.name) {
    nextRecord.name = (await decryptIfEncrypted({ value: record.name, clubId })) ?? record.name;
  }

  for (const [key, nested] of Object.entries(record)) {
    if (key === 'name') continue;
    if (nested && typeof nested === 'object') {
      nextRecord[key] = await maybeDecryptPlayerRefs(nested, clubId);
    } else if (Array.isArray(nested)) {
      nextRecord[key] = await maybeDecryptPlayerRefs(nested, clubId);
    }
  }

  return nextRecord;
};

const containsEncryptedPlayerName = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.some(containsEncryptedPlayerName);
  }
  if (!value || typeof value !== 'object') {
    return false;
  }

  const record = value as Record<string, unknown>;
  if (typeof record.name === 'string' && looksEncrypted(record.name)) {
    return true;
  }

  return Object.entries(record).some(([key, nested]) =>
    key !== 'name' && containsEncryptedPlayerName(nested)
  );
};

const hasEncryptedMatchPlayerNames = (match: Pick<Match, 'lineup' | 'reserve_players' | 'substitution_schedule' | 'substitutions'>): boolean =>
  containsEncryptedPlayerName(match.lineup) ||
  containsEncryptedPlayerName(match.reserve_players) ||
  containsEncryptedPlayerName(match.substitution_schedule) ||
  containsEncryptedPlayerName(match.substitutions);

// Coerce a player coming from the matches.lineup (sanitized shape) or from DB players row
const coercePlayerFromAny = (p: MatchPlayerRecord | null | undefined): Player => {
  if (!p) return p as unknown as Player;

  const normalizedPositions = normalizePositionPreferences(
    p.positionPreferencesByVariant && typeof p.positionPreferencesByVariant === 'object'
      ? { ...(p.positionPreferencesByVariant as Record<string, unknown>), variant: p.positionPreferenceVariant }
      : p.positions ?? p.positionPreferences
  );

  return {
    id: String(p.id),
    clubId: typeof p.clubId === 'string' ? p.clubId : (typeof p.club_id === 'string' ? p.club_id : ''),
    name: typeof p.name === 'string' ? p.name : '',
    number: String(p.number ?? ''),
    positionPreferences: normalizedPositions.preferences,
    positionPreferenceVariant: normalizedPositions.variant,
    positionPreferencesByVariant: normalizedPositions.variants,
    matchPositionOverride: p.matchPositionOverride === true,
    hasPositionPriority: normalizedPositions.hasExplicitPriorities || Boolean(p.hasPositionPriority),
    gender: p.gender === 'male' || p.gender === 'female' ? p.gender : null,
    isGoalkeeper: Boolean(p.isGoalkeeper),
    condition: typeof p.condition === 'number' ? p.condition : null,
    performance: typeof p.performance === 'number' ? p.performance : null,
    playingTime: typeof p.playingTime === 'number' ? p.playingTime : (typeof p.playing_time === 'number' ? p.playing_time : undefined),
    goals: typeof p.goals === 'number' ? p.goals : undefined,
    matchesPlayed: typeof p.matchesPlayed === 'number' ? p.matchesPlayed : (typeof p.matches_played === 'number' ? p.matches_played : 0),
    evaluations: Array.isArray(p.evaluations) ? p.evaluations : [],
    statistics: (p.statistics as PlayerStatistics | undefined) || undefined,
  };
};

const withAssignedPriority = (player: Player, position: Position): Player => ({
  ...player,
  assignedPositionPriority:
    typeof player.assignedPositionPriority === 'number'
      ? player.assignedPositionPriority
      : getPlayerPositionPriority(player, position),
});

const getStoredPlayerRefId = (value: unknown): string => {
  if (typeof value === 'string' || typeof value === 'number') {
    return normalizePlayerId(value);
  }
  if (value && typeof value === 'object' && 'id' in (value as MatchPlayerRecord)) {
    return normalizePlayerId((value as MatchPlayerRecord).id);
  }
  return '';
};

const fetchPlayersByStoredRefs = async (
  refs: unknown[],
  clubId?: string | null
): Promise<Record<string, Player>> => {
  const ids = Array.from(new Set(refs.map(getStoredPlayerRefId).filter(id => id.length > 0)));
  if (ids.length === 0) return {};

  try {
    const rows = await listPlayersApi({ ids });
    const decryptedRows = clubId
      ? await maybeDecryptPlayerRefs(rows, clubId)
      : rows;
    const playerMap: Record<string, Player> = {};
    asRecordArray(decryptedRows).forEach(row => {
      const player = coercePlayerFromAny(row);
      const id = normalizePlayerId(player.id);
      if (id) {
        playerMap[id] = { ...player, id };
      }
    });
    return playerMap;
  } catch {
    return {};
  }
};

const hydrateStoredLineup = async (
  lineup: unknown,
  clubId?: string | null
): Promise<Record<string, Player | null>> => {
  const entries = Object.entries((lineup as Record<string, unknown>) || {});
  const playerMap = await fetchPlayersByStoredRefs(entries.map(([, player]) => player), clubId);

  return Object.fromEntries(
    entries.map(([position, storedPlayer]) => {
      const id = getStoredPlayerRefId(storedPlayer);
      const storedRecord = storedPlayer && typeof storedPlayer === 'object'
        ? storedPlayer as MatchPlayerRecord
        : null;
      const storedOverride = storedRecord?.matchPositionOverride === true
        ? coercePlayerFromAny(storedRecord)
        : null;
      const player = storedOverride
        ? mergeMatchPositionOverride(playerMap[id], storedOverride)
        : id && playerMap[id]
          ? playerMap[id]
          : storedRecord
            ? coercePlayerFromAny(storedRecord)
            : null;

      return [
        position,
        player ? withAssignedPriority(player, position as Position) : null
      ];
    })
  );
};

const hydrateStoredPlayers = async (
  refs: unknown,
  clubId?: string | null
): Promise<Player[]> => {
  if (!Array.isArray(refs)) return [];

  const playerMap = await fetchPlayersByStoredRefs(refs, clubId);
  return refs
    .map(ref => {
      const id = getStoredPlayerRefId(ref);
      if (ref && typeof ref === 'object' && (ref as MatchPlayerRecord).matchPositionOverride === true) {
        return mergeMatchPositionOverride(playerMap[id], coercePlayerFromAny(ref as MatchPlayerRecord));
      }
      if (id && playerMap[id]) return playerMap[id];
      return ref && typeof ref === 'object' ? coercePlayerFromAny(ref as MatchPlayerRecord) : null;
    })
    .filter((player): player is Player => Boolean(player));
};

const sanitizeStoredPlayerRef = (player: Player): string | MatchPlayerRecord => player.matchPositionOverride
  ? {
      id: player.id,
      matchPositionOverride: true,
      positionPreferences: player.positionPreferences,
      positionPreferenceVariant: player.positionPreferenceVariant,
      positionPreferencesByVariant: player.positionPreferencesByVariant,
      hasPositionPriority: player.hasPositionPriority,
    }
  : player.id;

const sanitizeStoredLineup = (
  lineup?: Record<string, Player | null> | null
): Record<string, string | MatchPlayerRecord | null> | null | undefined => {
  if (!lineup) return lineup;
  return Object.fromEntries(
    Object.entries(lineup).map(([position, player]) => [
      position,
      player ? sanitizeStoredPlayerRef(player) : null,
    ])
  );
};

export const sanitizeStoredPlayers = (players?: Player[]): Array<string | MatchPlayerRecord> | undefined => {
  if (!Array.isArray(players)) return players;
  return players.map(sanitizeStoredPlayerRef);
};

const isStoredPlayerRecord = (value: unknown): value is Player =>
  Boolean(value && typeof value === 'object' && 'id' in (value as Record<string, unknown>));

const sanitizeStoredScheduleValue = (value: unknown): unknown => {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeStoredScheduleValue(item));
  }
  if (isStoredPlayerRecord(value)) {
    return value.id;
  }
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, nested]) => [
        key,
        sanitizeStoredScheduleValue(nested),
      ])
    );
  }
  return value;
};

const sanitizeStoredSubstitutions = (substitutions?: Substitution[]): Array<Record<string, unknown>> | undefined => {
  if (!Array.isArray(substitutions)) return substitutions as undefined;
  return substitutions.map((substitution) => ({
    ...substitution,
    playerOut: substitution.playerOut?.id ?? null,
    playerIn: substitution.playerIn?.id ?? null,
  }));
};

// Convert between database representation (singular) and
// internal representation (plural) of formation type
const toInternalFormationType = (
  type?: 'positions' | 'lines' | null
): 'positions' | 'lines' =>
  type === 'lines' ? 'lines' : 'positions';

const toDbFormationType = (
  type: 'positions' | 'lines'
): 'positions' | 'lines' => type;

type ClubIdCache = Map<string, string | null>;

const fetchTeamClubId = async (teamId: string, cache: ClubIdCache): Promise<string | null> => {
  if (!teamId) return null;
  if (cache.has(teamId)) return cache.get(teamId) ?? null;

  const team = await getTeamApi(teamId).catch(() => null);
  const clubId = team && typeof team === 'object' && 'club_id' in (team as Record<string, unknown>)
    ? ((team as Record<string, unknown>).club_id as string | null)
    : null;
  cache.set(teamId, clubId);
  return clubId;
};

const resolveMatchClubId = async (match: { club_id?: string | null; team_id?: string }, cache: ClubIdCache) => {
  if (match.club_id) return match.club_id;
  if (!match.team_id) return null;
  return fetchTeamClubId(String(match.team_id), cache);
};

// Utility function to generate a unique match key
const generateUniqueMatchKey = async (): Promise<string> => {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
};

// Zustand store containing all match related state and actions such as
// creating matches, managing lineups and tracking match time.

// Default match configuration. All durations are stored in seconds to simplify
// calculations throughout the application.
const DEFAULT_MATCH_CONFIG: MatchConfig = {
  quarterDuration: 17.5 * 60, // 17.5 minutes
  shortBreakDuration: 2 * 60,   // 2 minutes
  longBreakDuration: 5 * 60,    // 5 minutes
  substitutionInterval: 6 * 60, // 6 minutes
  numberOfQuarters: 4,          // 4 quarters
  formation_key: '',
  formation_type: 'positions',
  allowLowerPriority: true,
  forceRotation: true,
  optimizeIterations: 30
};

const DEFAULT_SUBSTITUTIONS_PER_QUARTER = 1;

const minutesFromMaybeSeconds = (value?: number | null): number | undefined => {
  if (value === undefined || value === null) return undefined;
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return undefined;
  return numeric > 180 ? numeric / 60 : numeric;
};

export const fetchMatchTypeMeta = async (matchTypeKey?: string | null): Promise<MatchTypeMetadata | null> => {
  if (!matchTypeKey) return null;
  try {
    return await getMatchTypeMetaApi(matchTypeKey);
  } catch {
    return null;
  }
};

const fetchMatchTypeMetaFromFormation = async (formationKey?: string | null): Promise<MatchTypeMetadata | null> => {
  if (!formationKey) return null;

  const formation = await getFormationApi(formationKey).catch(() => null);
  if (!formation || typeof formation !== 'object') {
    return null;
  }

  const source = formation as Record<string, unknown>;
  const matchTypeKey =
    (typeof source.field === 'string' ? source.field : null) ??
    (typeof source.match_type_key === 'string' ? source.match_type_key : null) ??
    (typeof source.matchTypeKey === 'string' ? source.matchTypeKey : null);
  if (typeof matchTypeKey !== 'string' || matchTypeKey.trim().length === 0) {
    return null;
  }

  return fetchMatchTypeMeta(matchTypeKey.trim());
};

const deriveMatchConfigFromMeta = (
  baseConfig: MatchConfig,
  meta: MatchTypeMetadata | null,
  substitutionsPerQuarter: number | null | undefined
): MatchConfig => {
  if (!meta) {
    return baseConfig;
  }

  const updatedConfig: MatchConfig = { ...baseConfig };

  if (typeof meta.quarters === 'number' && Number.isFinite(meta.quarters) && meta.quarters > 0) {
    updatedConfig.numberOfQuarters = meta.quarters;
  }

  const quarterMinutes = minutesFromMaybeSeconds(
    meta.quarter_time ?? (
      meta.total_playing_time && meta.quarters
        ? meta.total_playing_time / meta.quarters
        : undefined
    )
  );

  if (quarterMinutes !== undefined) {
    updatedConfig.quarterDuration = Math.max(60, Math.round(quarterMinutes * 60));
  }

  const sanitizedSubsPerQuarter =
    typeof substitutionsPerQuarter === 'number' && Number.isFinite(substitutionsPerQuarter)
      ? Math.max(0, Math.floor(substitutionsPerQuarter))
      : null;

  const effectiveSubsPerQuarter = sanitizedSubsPerQuarter;

  if (effectiveSubsPerQuarter !== null && quarterMinutes !== undefined) {
    const intervalSeconds = Math.round((quarterMinutes * 60) / (effectiveSubsPerQuarter + 1));
    if (Number.isFinite(intervalSeconds) && intervalSeconds > 0) {
      updatedConfig.substitutionInterval = intervalSeconds;
    }
  }

  return updatedConfig;
};


/**
 * Definieert de vorm van de wedstrijd-state die door Zustand wordt beheerd.
 */
interface MatchState {
  /** Array van alle wedstrijden die voor de huidige context zijn geladen (bijv. voor een specifiek team). */
  matches: Match[];
  /** Het ID van de momenteel geselecteerde wedstrijd. Null als er geen wedstrijd is geselecteerd. */
  currentMatchId: string | null;
  /** Configuratie-instellingen die van toepassing zijn op wedstrijden (bijv. duur, intervallen). */
  matchConfig: MatchConfig;
  /** Het ID van de wedstrijd die momenteel wordt bewerkt (bijv. de opstelling). Null indien niet in bewerkingsmodus. */
  editingMatchId: string | null;
  /** De opstelling van de wedstrijd die momenteel wordt bewerkt. Null indien niet in bewerkingsmodus of geen opstelling. */
  editingMatchLineup: Record<Position, Player | null> | null;
  /** De lijst met wissels voor de wedstrijd die momenteel wordt bewerkt. Null indien niet in bewerkingsmodus. */
  editingMatchSubstitutions: Substitution[] | null;

  /**
   * Creëert een nieuwe wedstrijd en slaat deze op in de database.
   * @param matchData - Gegevens voor de nieuwe wedstrijd, exclusief het 'id'.
   * @returns Een promise die resulteert in het ID van de nieuw aangemaakte wedstrijd.
   */
  createMatch: (matchData: Omit<Match, 'id'>) => Promise<string>;
  /**
   * Werkt een bestaande wedstrijd bij met de verstrekte gedeeltelijke gegevens.
   * @param id - Het ID van de wedstrijd die moet worden bijgewerkt.
   * @param updates - Een object met de velden die moeten worden bijgewerkt.
   */
  updateMatch: (id: string, updates: Partial<Match>) => Promise<void>;
  /**
   * Verwijdert een wedstrijd uit de store en de database.
   * @param id - Het ID van de te verwijderen wedstrijd.
   */
  deleteMatch: (id: string) => Promise<void>;
  /**
   * Stelt de huidige wedstrijd in en laadt indien nodig de volledige details.
   * @param id - Het ID van de wedstrijd om als huidig in te stellen, of null om te wissen.
   */
  setCurrentMatch: (id: string | null) => Promise<void>;
  /**
   * Stelt de lijst met wedstrijden rechtstreeks in de store in. Wordt gebruikt voor initiële laadtijden of vernieuwingen.
   * @param matches - Een array van Match-objecten.
   */
  setMatches: (matches: Match[]) => void;
  /** Loads matches that have a saved lineup for the given team. */
  loadMatchesWithLineups: (teamId: string) => Promise<Match[]>;

  /**
   * Wijst een speler toe aan een specifieke positie in de opstelling van de momenteel bewerkte wedstrijd.
   * @param matchId - Het ID van de te bewerken wedstrijd.
   * @param position - De positie waaraan de speler moet worden toegewezen.
   * @param player - Het spelerobject om toe te wijzen.
   */
  assignPlayerToPosition: (matchId: string, position: Position, player: Player) => Promise<void>;
  /**
   * Verwijdert een speler van een specifieke positie in de opstelling van de momenteel bewerkte wedstrijd.
   * @param matchId - Het ID van de te bewerken wedstrijd.
   * @param position - De te wissen positie.
   */
  removePlayerFromPosition: (matchId: string, position: Position) => Promise<void>;

  /**
   * Stelt de opstelling in voor de wedstrijd die momenteel wordt bewerkt.
   * @param lineup - Het opstellingsobject dat posities aan spelers koppelt.
   */
  setEditingMatchLineup: (lineup: Record<Position, Player | null>) => void;

  /** Stelt de reservespelers voor de opgegeven wedstrijd rechtstreeks in het geheugen in. */
  setReservePlayers: (matchId: string, players: Player[]) => void;

  /**
   * Voegt een nieuwe wissel toe aan de momenteel bewerkte wedstrijd.
   * @param matchId - Het ID van de wedstrijd.
   * @param substitution - De wisselgegevens, exclusief 'id'.
   */
  addSubstitution: (matchId: string, substitution: Omit<Substitution, 'id'>, occupation?: { lineup: Record<Position, Player | null>; reserves: Player[] }) => Promise<void>;
  /**
   * Werkt een bestaande wissel bij in de momenteel bewerkte wedstrijd.
   * @param matchId - Het ID van de wedstrijd.
   * @param id - Het ID van de te updaten wissel.
   * @param updates - Gedeeltelijke wisselgegevens.
   */
  updateSubstitution: (matchId: string, id: string, updates: Partial<Substitution>) => Promise<void>;
  /**
   * Verwijdert een wissel uit de momenteel bewerkte wedstrijd.
   * @param matchId - Het ID van de wedstrijd.
   * @param id - Het ID van de te verwijderen wissel.
   */
  deleteSubstitution: (matchId: string, id: string) => Promise<void>;

  /**
   * Werkt de huidige verstreken tijd van een wedstrijd bij.
   * @param matchId - Het ID van de wedstrijd.
   * @param time - De nieuwe wedstrijdtijd in seconden.
   */
  updateMatchTime: (matchId: string, time: number) => Promise<void>;
  /**
   * Werkt de status van een wedstrijd bij (bijv. 'upcoming', 'in_progress', 'completed').
   * @param matchId - Het ID van de wedstrijd.
   * @param status - De nieuwe status.
   */
  updateMatchStatus: (matchId: string, status: Match['status']) => Promise<void>;
  /**
   * Werkt het huidige kwart van een wedstrijd bij.
   * @param matchId - Het ID van de wedstrijd.
   * @param quarter - Het nieuwe kwartnummer.
   */
  updateMatchQuarter: (matchId: string, quarter: number) => Promise<void>;

  /**
   * Werkt de algemene wedstrijdconfiguratie-instellingen bij.
   * @param config - Gedeeltelijk MatchConfig-object met nieuwe instellingen.
   */
  updateMatchConfig: (config: Partial<MatchConfig>) => void;

  /** Haalt het volledige object op voor de momenteel geselecteerde wedstrijd, inclusief eventuele openstaande bewerkingen. */
  getCurrentMatch: () => Match | null;
  /**
   * Berekent de tijd voor de volgende geplande wissel op basis van de laatste.
   * @param matchId - Het ID van de wedstrijd.
   * @returns De tijdstempel voor de volgende wissel, of null indien niet van toepassing.
   */
  getNextSubstitutionTime: (matchId: string) => number | null;
  /**
   * Berekent de speeltijdstatistieken voor een specifieke speler in een bepaalde wedstrijd.
   * @param matchId - Het ID van de wedstrijd.
   * @param playerId - Het ID van de speler.
   * @returns Een object met `playingTime` (in seconden) en `playingTimePercentage`.
   */
  getPlayerStatsForMatch: (matchId: string, playerId: string) => {
    playingTime: number;
    playingTimePercentage: number;
  };
  /** Herstelt de store naar de oorspronkelijke staat. */
  reset: () => void;
}

/**
 * Defines the shape of the match state managed by Zustand.
 */
interface MatchState {
  /** Array van alle wedstrijden die voor de huidige context zijn geladen (bijv. voor een specifiek team). */
  matches: Match[];
  /** Het ID van de momenteel geselecteerde wedstrijd. Null als er geen wedstrijd is geselecteerd. */
  currentMatchId: string | null;
  /** Configuratie-instellingen die van toepassing zijn op wedstrijden (bijv. duur, intervallen). */
  matchConfig: MatchConfig;
  /** Het ID van de wedstrijd die momenteel wordt bewerkt (bijv. de opstelling). Null indien niet in bewerkingsmodus. */
  editingMatchId: string | null;
  /** De opstelling van de wedstrijd die momenteel wordt bewerkt. Null indien niet in bewerkingsmodus of geen opstelling. */
  editingMatchLineup: Record<Position, Player | null> | null;
  /** De lijst met wissels voor de wedstrijd die momenteel wordt bewerkt. Null indien niet in bewerkingsmodus. */
  editingMatchSubstitutions: Substitution[] | null;

  /**
   * Creëert een nieuwe wedstrijd en slaat deze op in de database.
   * @param matchData - Gegevens voor de nieuwe wedstrijd, exclusief het 'id'.
   * @returns Een promise die resulteert in het ID van de nieuw aangemaakte wedstrijd.
   */
  createMatch: (matchData: Omit<Match, 'id'>) => Promise<string>;
  /**
   * Werkt een bestaande wedstrijd bij met de verstrekte gedeeltelijke gegevens.
   * @param id - Het ID van de wedstrijd die moet worden bijgewerkt.
   * @param updates - Een object met de velden die moeten worden bijgewerkt.
   */
  updateMatch: (id: string, updates: Partial<Match>) => Promise<void>;
  /**
   * Verwijdert een wedstrijd uit de store en de database.
   * @param id - Het ID van de te verwijderen wedstrijd.
   */
  deleteMatch: (id: string) => Promise<void>;
  /**
   * Stelt de huidige wedstrijd in en laadt indien nodig de volledige details.
   * @param id - Het ID van de wedstrijd om als huidig in te stellen, of null om te wissen.
   */
  setCurrentMatch: (id: string | null) => Promise<void>;
  /**
   * Stelt de lijst met wedstrijden rechtstreeks in de store in. Wordt gebruikt voor initiële laadtijden of vernieuwingen.
   * @param matches - Een array van Match-objecten.
   */
  setMatches: (matches: Match[]) => void;
  /** Loads matches that have a saved lineup for the given team. */
  loadMatchesWithLineups: (teamId: string) => Promise<Match[]>;

  /**
   * Wijst een speler toe aan een specifieke positie in de opstelling van de momenteel bewerkte wedstrijd.
   * @param matchId - Het ID van de te bewerken wedstrijd.
   * @param position - De positie waaraan de speler moet worden toegewezen.
   * @param player - Het spelerobject om toe te wijzen.
   */
  assignPlayerToPosition: (matchId: string, position: Position, player: Player) => Promise<void>;
  /**
   * Verwijdert een speler van een specifieke positie in de opstelling van de momenteel bewerkte wedstrijd.
   * @param matchId - Het ID van de te bewerken wedstrijd.
   * @param position - De te wissen positie.
   */
  removePlayerFromPosition: (matchId: string, position: Position) => Promise<void>;

  /**
   * Stelt de opstelling in voor de wedstrijd die momenteel wordt bewerkt.
   * @param lineup - Het opstellingsobject dat posities aan spelers koppelt.
   */
  setEditingMatchLineup: (lineup: Record<Position, Player | null>) => void;

  /** Stelt de reservespelers voor de opgegeven wedstrijd rechtstreeks in het geheugen in. */
  setReservePlayers: (matchId: string, players: Player[]) => void;

  /**
   * Voegt een nieuwe wissel toe aan de momenteel bewerkte wedstrijd.
   * @param matchId - Het ID van de wedstrijd.
   * @param substitution - De wisselgegevens, exclusief 'id'.
   */
  addSubstitution: (matchId: string, substitution: Omit<Substitution, 'id'>, occupation?: { lineup: Record<Position, Player | null>; reserves: Player[] }) => Promise<void>;
  /**
   * Werkt een bestaande wissel bij in de momenteel bewerkte wedstrijd.
   * @param matchId - Het ID van de wedstrijd.
   * @param id - Het ID van de te updaten wissel.
   * @param updates - Gedeeltelijke wisselgegevens.
   */
  updateSubstitution: (matchId: string, id: string, updates: Partial<Substitution>) => Promise<void>;
  /**
   * Verwijdert een wissel uit de momenteel bewerkte wedstrijd.
   * @param matchId - Het ID van de wedstrijd.
   * @param id - Het ID van de te verwijderen wissel.
   */
  deleteSubstitution: (matchId: string, id: string) => Promise<void>;

  /**
   * Werkt de huidige verstreken tijd van een wedstrijd bij.
   * @param matchId - Het ID van de wedstrijd.
   * @param time - De nieuwe wedstrijdtijd in seconden.
   */
  updateMatchTime: (matchId: string, time: number) => Promise<void>;
  /**
   * Werkt de status van een wedstrijd bij (bijv. 'upcoming', 'in_progress', 'completed').
   * @param matchId - Het ID van de wedstrijd.
   * @param status - De nieuwe status.
   */
  updateMatchStatus: (matchId: string, status: Match['status']) => Promise<void>;
  /**
   * Werkt het huidige kwart van een wedstrijd bij.
   * @param matchId - Het ID van de wedstrijd.
   * @param quarter - Het nieuwe kwartnummer.
   */
  updateMatchQuarter: (matchId: string, quarter: number) => Promise<void>;

  /**
   * Werkt de algemene wedstrijdconfiguratie-instellingen bij.
   * @param config - Gedeeltelijk MatchConfig-object met nieuwe instellingen.
   */
  updateMatchConfig: (config: Partial<MatchConfig>) => void;

  /** Haalt het volledige object op voor de momenteel geselecteerde wedstrijd, inclusief eventuele openstaande bewerkingen. */
  getCurrentMatch: () => Match | null;
  /**
   * Berekent de tijd voor de volgende geplande wissel op basis van de laatste.
   * @param matchId - Het ID van de wedstrijd.
   * @returns De tijdstempel voor de volgende wissel, of null indien niet van toepassing.
   */
  getNextSubstitutionTime: (matchId: string) => number | null;
  /**
   * Berekent de speeltijdstatistieken voor een specifieke speler in een bepaalde wedstrijd.
   * @param matchId - Het ID van de wedstrijd.
   * @param playerId - Het ID van de speler.
   * @returns Een object met `playingTime` (in seconden) en `playingTimePercentage`.
   */
  getPlayerStatsForMatch: (matchId: string, playerId: string) => {
    playingTime: number;
    playingTimePercentage: number;
  };
  /** Herstelt de store naar de oorspronkelijke staat. */
  reset: () => void;
}

const getInitialState = (): Pick<
  MatchState,
  | 'matches'
  | 'currentMatchId'
  | 'matchConfig'
  | 'editingMatchId'
  | 'editingMatchLineup'
  | 'editingMatchSubstitutions'
> => ({
  matches: [],
  currentMatchId: null,
  matchConfig: { ...DEFAULT_MATCH_CONFIG },
  editingMatchId: null,
  editingMatchLineup: null,
  editingMatchSubstitutions: null
});

/**
 * Zustand-store voor het beheren van alle wedstrijdgerelateerde state en acties.
 * Dit omvat het aanmaken/updaten/verwijderen van wedstrijden, het beheren van opstellingen,
 * het bijhouden van wedstrijdtijd en -status, en het afhandelen van wissels.
 */
export const useMatchStore = create<MatchState>((set, get) => ({
  ...getInitialState(),
  setMatches: (matches: Match[]) => {
    set({ matches });
    try {
      if (typeof sessionStorage === 'undefined') return;
      const storedMatchId = sessionStorage.getItem('currentMatchId');
      if (!storedMatchId) return;
      const exists = matches.some(m => m.id === storedMatchId);
      if (exists && get().currentMatchId !== storedMatchId) {
        void get().setCurrentMatch(storedMatchId);
      }
      // A loading/filtered list is not evidence that the stored match was deleted.
    } catch {
      // ignore storage failures
    }
  },
  loadMatchesWithLineups: async (teamId: string) => {
    const existing = get().matches.filter(
      m => m.team_id === teamId && m.lineup && Object.keys(m.lineup).length > 0
    );
    try {
      const data = await listMatchesApi({
        team_id: teamId,
        has_lineup: true
      });

      const clubIdCache: ClubIdCache = new Map();
      const decryptedData: MatchApiRecord[] = await Promise.all(
        asRecordArray(data).map(async (m) => {
          let knhbId = asNullableString(m.knhb_match_id);
          let homeTeam = asString(m.home_team);
          let awayTeam = asString(m.away_team);
          let location = asString(m.location);
          const clubId = await resolveMatchClubId(m, clubIdCache);

          if (clubId) {
            if (knhbId) {
              knhbId = (await decryptIfEncrypted({ value: knhbId, clubId })) ?? knhbId;
            }
            if (homeTeam) {
              homeTeam = (await decryptIfEncrypted({ value: homeTeam, clubId })) ?? homeTeam;
            }
            if (awayTeam) {
              awayTeam = (await decryptIfEncrypted({ value: awayTeam, clubId })) ?? awayTeam;
            }
            if (location) {
              location = (await decryptIfEncrypted({ value: location, clubId })) ?? location;
            }
            if (m.lineup) {
              m.lineup = await maybeDecryptPlayerRefs(m.lineup, clubId) as MatchApiRecord;
            }
            if (m.reserve_players) {
              m.reserve_players = await maybeDecryptPlayerRefs(m.reserve_players, clubId) as MatchApiRecord[];
            }
            if (m.substitution_schedule) {
              m.substitution_schedule = await maybeDecryptPlayerRefs(m.substitution_schedule, clubId) as MatchApiRecord;
            }
            if (m.substitutions) {
              m.substitutions = await maybeDecryptPlayerRefs(m.substitutions, clubId) as MatchApiRecord[];
            }
          }
          return {
            ...m,
            club_id: clubId ?? asNullableString(m.club_id),
            knhb_match_id: knhbId,
            home_team: homeTeam,
            away_team: awayTeam,
            location,
          } as MatchApiRecord;
        })
      );

      const fetched: Match[] = await Promise.all(decryptedData.map(async (m) => {
        const standins = Array.isArray(m.standin_players) ? m.standin_players : [];
        return {
          id: asString(m.id),
          updated_at: typeof m.updated_at === 'string' ? m.updated_at : undefined,
          team_id: asString(m.team_id),
          club_id: asNullableString(m.club_id),
          date: asString(m.date),
          location: asString(m.location),
          field: asString(m.field, 'outdoor_full'),
          is_home: asNullableBoolean(m.is_home),
          formation_key: asString(m.formation_key),
          preparation_settings: m.preparation_settings as Match['preparation_settings'],
          home_team: asString(m.home_team),
          home_team_logo: typeof m.home_team_logo === 'string' ? m.home_team_logo : undefined,
          away_team: asString(m.away_team),
          away_team_logo: typeof m.away_team_logo === 'string' ? m.away_team_logo : undefined,
          lineup: await hydrateStoredLineup(m.lineup, asNullableString(m.club_id)),
          reserve_players: await hydrateStoredPlayers(m.reserve_players, asNullableString(m.club_id)),
          unavailable_players: Array.isArray(m.unavailable_players) ? m.unavailable_players.filter((id): id is string => typeof id === 'string') : [],
          substitutions: Array.isArray(m.substitutions) ? (m.substitutions as Substitution[]) : [],
          substitution_schedule: m.substitution_schedule ?? null,
          substitutions_per_quarter: asNullableNumber(m.substitutions_per_quarter),
          match_type_meta: (m.match_type_meta as MatchTypeMetadata | null) ?? null,
          player_participation: (m.player_participation as Match['player_participation']) ?? null,
          current_quarter: asNumber(m.current_quarter, 1),
          match_time: asNumber(m.match_time, 0),
          formation_type: toInternalFormationType((typeof m.formation_type === 'string' ? m.formation_type : null) as 'positions' | 'lines' | null),
          status:
            m.status === 'inProgress'
              ? 'in_progress'
              : ((m.status as Match['status']) || 'upcoming'),
          match_key: asString(m.match_key),
          knhb_match_id: asNullableString(m.knhb_match_id) ?? undefined,
          media: typeof m.media === 'number' ? m.media : 0,
          standin_players: standins as string[],
        } as Match;
      }));
      set(state => ({
        matches: [
          ...state.matches,
          ...fetched.filter(f => !state.matches.some(e => e.id === f.id)),
        ],
      }));
      return fetched;
    } catch {
      return existing;
    }
  },
  setEditingMatchLineup: (lineup: Record<Position, Player | null>) => {
    set({ editingMatchLineup: lineup });
  },
  setReservePlayers: (matchId: string, players: Player[]) => {
    set(state => ({
      matches: state.matches.map(m =>
        m.id === matchId
          ? {
              ...m,
              reserve_players: players.map(player => {
                const storedOverride = [
                  ...Object.values(m.lineup || {}),
                  ...(m.reserve_players || []),
                ].find(candidate => candidate?.id === player.id && candidate.matchPositionOverride);
                return storedOverride && !player.matchPositionOverride
                  ? mergeMatchPositionOverride(player, storedOverride)
                  : player;
              }),
            }
          : m
      )
    }));
  },
  createMatch: async (matchData: Omit<Match, 'id'>) => {
    try {
      const matchKey = await generateUniqueMatchKey();

      // Get the team's default formation key and club_id
      const teamData = await getTeamApi(matchData.team_id);
      const teamRecord =
        teamData && typeof teamData === 'object' ? (teamData as Record<string, unknown>) : null;

      if (!teamRecord) throw new Error('Team settings could not be loaded');
      const teamDefaults = parseLastMatchDefaults(teamRecord.last_match_defaults);
      const resolvedFormationKey = (() => {
        if (typeof matchData.formation_key === 'string' && matchData.formation_key.trim().length > 0) {
          return matchData.formation_key.trim();
        }
        const indoor = matchData.field ? matchData.field.startsWith('indoor') : teamRecord.playing_environment === 'indoor';
        if (indoor) return typeof teamRecord.default_indoor_formation_key === 'string' ? teamRecord.default_indoor_formation_key : '';
        if (
          typeof teamRecord?.default_formation_key === 'string' &&
          teamRecord.default_formation_key.trim().length > 0
        ) {
          return teamRecord.default_formation_key.trim();
        }
        return teamDefaults?.formation_key ?? '';
      })();

      // Create a completely new, plain object with only primitive values
      let sanitizedSubsPerQuarter =
        typeof matchData.substitutions_per_quarter === 'number' && Number.isFinite(matchData.substitutions_per_quarter)
          ? Math.max(0, Math.floor(matchData.substitutions_per_quarter))
          : teamDefaults?.substitutions_per_quarter ?? null;

      if (sanitizedSubsPerQuarter === null) {
        let formationSubs: number | null = null;

        if (resolvedFormationKey) {
          const formationRow = await getFormationApi(resolvedFormationKey).catch(() => null);
          if (
            formationRow &&
            typeof formationRow === 'object' &&
            typeof (formationRow as Record<string, unknown>).substitutions_per_quarter === 'number' &&
            Number.isFinite((formationRow as Record<string, unknown>).substitutions_per_quarter)
          ) {
            formationSubs = (formationRow as Record<string, unknown>).substitutions_per_quarter as number;
          }
        }

        if (typeof formationSubs === 'number' && Number.isFinite(formationSubs)) {
          sanitizedSubsPerQuarter = Math.max(0, Math.floor(formationSubs));
        } else {
          sanitizedSubsPerQuarter = DEFAULT_SUBSTITUTIONS_PER_QUARTER;
        }
      }

      let encryptedKnhbId: string | null = null;
      let encryptedHomeTeam: string = String(matchData.home_team || '');
      let encryptedAwayTeam: string = String(matchData.away_team || '');
      let encryptedLocation: string = String(matchData.location || '');

      const clubId = typeof teamRecord?.club_id === 'string' ? teamRecord.club_id : null;

      if (clubId) {
        if (matchData.knhb_match_id) {
          try {
            encryptedKnhbId = await encryptValue({
              plaintext: String(matchData.knhb_match_id),
              clubId,
              teamId: String(matchData.team_id)
            });
          } catch (error) {
            console.error('Failed to encrypt knhb_match_id', error);
            encryptedKnhbId = String(matchData.knhb_match_id);
          }
        }

        if (matchData.home_team) {
          try {
            encryptedHomeTeam = await encryptValue({
              plaintext: String(matchData.home_team),
              clubId,
              teamId: String(matchData.team_id)
            });
          } catch (error) {
            console.error('Failed to encrypt home_team', error);
            // keep original
          }
        }

        if (matchData.away_team) {
          try {
            encryptedAwayTeam = await encryptValue({
              plaintext: String(matchData.away_team),
              clubId,
              teamId: String(matchData.team_id)
            });
          } catch (error) {
            console.error('Failed to encrypt away_team', error);
            // keep original
          }
        }

        if (matchData.location) {
          try {
            encryptedLocation = await encryptValue({
              plaintext: String(matchData.location),
              clubId,
              teamId: String(matchData.team_id)
            });
          } catch (error) {
            console.error('Failed to encrypt location', error);
            // keep original
          }
        }
      }

      const insertData = {
        team_id: String(matchData.team_id),
        club_id: clubId ?? null,
        date: new Date(matchData.date).toISOString(),
        location: encryptedLocation,
        field: String(matchData.field || 'outdoor_full') as 'outdoor_full' | 'indoor' | 'outdoor_half',
        is_home: asNullableBoolean(matchData.is_home),
        home_team: encryptedHomeTeam,
        away_team: encryptedAwayTeam,
        home_team_logo: typeof matchData.home_team_logo === 'string' ? String(matchData.home_team_logo) : null,
        away_team_logo: typeof matchData.away_team_logo === 'string' ? String(matchData.away_team_logo) : null,
        formation_key: resolvedFormationKey,
        preparation_settings: matchData.preparation_settings ?? { planning_options: teamDefaults?.planning_options ?? defaultPlanningOptions, strategy: teamDefaults?.strategy ?? { level: 'standard', rhythm: String(sanitizedSubsPerQuarter), rhythms: {}, assignments: {} } },
        formation_type: toDbFormationType(matchData.formation_type || 'positions'),
        status: 'upcoming',
        current_quarter: 1,
        match_time: 0,
        match_key: matchKey,
        knhb_match_id: encryptedKnhbId,
        lineup: {},
        reserve_players: [],
        substitutions: [],
        substitution_schedule: null,
        substitutions_per_quarter: sanitizedSubsPerQuarter
      };

      // Perform the insert with our clean data
      const createdPayload = await createMatchApi(insertData);
      if (!createdPayload || typeof createdPayload !== 'object') {
        throw new Error('createMatch returned an invalid payload');
      }
      const data = createdPayload as MatchApiRecord;

      let matchTypeMeta = matchData.match_type_meta ?? null;
      if (!matchTypeMeta) {
        matchTypeMeta = await fetchMatchTypeMeta(insertData.field);
      }
      if (!matchTypeMeta) {
        matchTypeMeta = await fetchMatchTypeMetaFromFormation(insertData.formation_key);
      }
      const dbSubsPerQuarter = asNullableNumber(data.substitutions_per_quarter);

      const hydratedLineup = await hydrateStoredLineup(
        data.lineup,
        asNullableString(data.club_id) ?? clubId ?? null
      );
      const hydratedReservePlayers = await hydrateStoredPlayers(
        data.reserve_players,
        asNullableString(data.club_id) ?? clubId ?? null
      );

      const match = {
        id: asString(data.id),
        updated_at: typeof data.updated_at === 'string' ? data.updated_at : undefined,
        team_id: asString(data.team_id),
        club_id: asNullableString(data.club_id) ?? clubId ?? null,
        date: asString(data.date),
        location: asString(data.location),
        field: asString(data.field, 'outdoor_full'),
        is_home: asNullableBoolean(data.is_home),
        formation_key: asString(data.formation_key),
        preparation_settings: data.preparation_settings as Match['preparation_settings'],
        home_team: asString(data.home_team),
        away_team: asString(data.away_team),
        home_team_logo: typeof data.home_team_logo === 'string' ? data.home_team_logo : undefined,
        away_team_logo: typeof data.away_team_logo === 'string' ? data.away_team_logo : undefined,
        lineup: hydratedLineup,
        reserve_players: hydratedReservePlayers,
        substitutions: Array.isArray(data.substitutions) ? (data.substitutions as Substitution[]) : [],
        substitution_schedule: (data.substitution_schedule as Match['substitution_schedule']) ?? null,
        substitutions_per_quarter: dbSubsPerQuarter,
        match_type_meta: matchTypeMeta,
        player_participation: (data.player_participation as Match['player_participation']) ?? null,
        current_quarter: asNumber(data.current_quarter, 1),
        match_time: asNumber(data.match_time, 0),
        formation_type: toInternalFormationType((typeof data.formation_type === 'string' ? data.formation_type : null) as 'positions' | 'lines' | null),
        status: 'upcoming' as const,
        match_key: asString(data.match_key),
        knhb_match_id: asNullableString(data.knhb_match_id) ?? undefined,
        media: typeof data.media === 'number' ? data.media : 0,
      } as Match;

      set((state) => ({
        matches: [...state.matches, match],
        matchConfig:
          state.currentMatchId === data.id
            ? deriveMatchConfigFromMeta(
              {
                ...state.matchConfig,
                formation_key: asString(data.formation_key),
                formation_type: toInternalFormationType((typeof data.formation_type === 'string' ? data.formation_type : null) as 'positions' | 'lines' | null)
              },
              matchTypeMeta,
              dbSubsPerQuarter
            )
            : state.matchConfig
      }));

      return asString(data.id);
    } catch (error) {
      throw error;
    }
  },
  updateMatch: async (id: string, updates: Partial<Match>) => {
    try {
      const loadedMatch = get().matches.find(match => match.id === id);
      if (loadedMatch && plannedMatchPlayerIds(loadedMatch)
        && ((loadedMatch.status === 'upcoming' && ['lineup', 'reserve_players', 'unavailable_players', 'preparation_settings', 'field', 'match_type_meta', 'substitutions_per_quarter'].some(key => key in updates))
          || ('substitution_schedule' in updates && !updates.substitution_schedule))) {
        const team = await getTeamApi(loadedMatch.team_id);
        if (!team || typeof team !== 'object' || !Array.isArray((team as Record<string, unknown>).players)) {
          throw new Error('Team players could not be loaded');
        }
        const roster = ((team as Record<string, unknown>).players as Array<string | Player>).map(player =>
          typeof player === 'string' ? { id: player } as Player : player);
        updates = retainPlannedSelection(loadedMatch, roster, updates);
      }
      if (loadedMatch && ('lineup' in updates || 'reserve_players' in updates)) {
        const preparation = { ...loadedMatch.preparation_settings, ...updates.preparation_settings };
        const overrides = matchPositionSettings({ ...loadedMatch, ...updates, preparation_settings: { ...preparation, match_position_overrides: { ...matchPositionSettings(loadedMatch), ...preparation.match_position_overrides } } });
        if (Object.keys(overrides).length) updates = { ...updates, preparation_settings: { ...preparation, match_position_overrides: overrides } };
      }
      const dbUpdates = { ...updates } as MatchUpdatePayload;
      if (loadedMatch?.status === 'upcoming' && loadedMatch.updated_at && !('expected_updated_at' in dbUpdates)) {
        dbUpdates.expected_updated_at = loadedMatch.updated_at;
      }
      if ('lineup' in updates) {
        dbUpdates.lineup = sanitizeStoredLineup(updates.lineup);
      }
      if ('reserve_players' in updates) {
        dbUpdates.reserve_players = sanitizeStoredPlayers(updates.reserve_players);
      }
      if ('substitution_schedule' in updates) {
        dbUpdates.substitution_schedule = updates.substitution_schedule
          ? sanitizeStoredScheduleValue(updates.substitution_schedule) as Record<string, unknown>
          : null;
      }
      if ('substitutions' in updates) {
        dbUpdates.substitutions = sanitizeStoredSubstitutions(updates.substitutions);
      }
      if ('formation_type' in updates && updates.formation_type) {
        dbUpdates.formation_type = toDbFormationType(
          updates.formation_type as 'positions' | 'lines'
        );
      }

      let sanitizedSubstitutionsForUpdate: number | null | undefined;
      if ('substitutions_per_quarter' in updates) {
        const value = updates.substitutions_per_quarter;
        sanitizedSubstitutionsForUpdate =
          typeof value === 'number' && Number.isFinite(value)
            ? Math.max(0, Math.floor(value))
            : value === null
              ? null
              : undefined;

        if (sanitizedSubstitutionsForUpdate !== undefined) {
          dbUpdates.substitutions_per_quarter = sanitizedSubstitutionsForUpdate;
        } else {
          delete (dbUpdates as Record<string, unknown>).substitutions_per_quarter;
        }
      }

      let nextMatchTypeMeta: MatchTypeMetadata | null | undefined = updates.match_type_meta;
      if ('field' in updates && typeof updates.field === 'string' && updates.field !== get().matches.find(m => m.id === id)?.field) {
        nextMatchTypeMeta = await fetchMatchTypeMeta(updates.field);
        if (!nextMatchTypeMeta) {
          const formationKeyForMeta =
            typeof updates.formation_key === 'string' && updates.formation_key.trim().length > 0
              ? updates.formation_key.trim()
              : get().matches.find(m => m.id === id)?.formation_key;
          nextMatchTypeMeta = await fetchMatchTypeMetaFromFormation(formationKeyForMeta);
        }
      }

      const clubIdCache: ClubIdCache = new Map();
      let matchMeta: { team_id?: string; club_id?: string | null; substitution_schedule?: unknown } | null =
        loadedMatch?.status === 'upcoming' && loadedMatch.club_id && loadedMatch.team_id
          && 'substitution_schedule' in loadedMatch ? loadedMatch : null;
      const getMatchMeta = async () => {
        if (matchMeta) return matchMeta;
        const matchData = await getMatchApi(id);
        matchMeta = (matchData && typeof matchData === 'object'
          ? (matchData as { team_id?: string; club_id?: string | null; substitution_schedule?: unknown })
          : null);
        return matchMeta;
      };

      if ('home_team' in updates && updates.home_team) {
        const matchData = await getMatchMeta();
        const clubId =
          matchData?.club_id ??
          (matchData?.team_id ? await fetchTeamClubId(String(matchData.team_id), clubIdCache) : null);
        if (clubId) {
          try {
            dbUpdates.home_team = await encryptValue({
              plaintext: updates.home_team,
              clubId,
              teamId: String(matchData?.team_id || '')
            });
          } catch {
            // if fail, use original
          }
        }
      }

      if ('away_team' in updates && updates.away_team) {
        const matchData = await getMatchMeta();
        const clubId =
          matchData?.club_id ??
          (matchData?.team_id ? await fetchTeamClubId(String(matchData.team_id), clubIdCache) : null);

        if (clubId) {
          try {
            dbUpdates.away_team = await encryptValue({
              plaintext: updates.away_team,
              clubId,
              teamId: String(matchData?.team_id || '')
            });
          } catch {
            // if fail, use original
          }
        }
      }

      if ('location' in updates && updates.location) {
        const matchData = await getMatchMeta();
        const clubId =
          matchData?.club_id ??
          (matchData?.team_id ? await fetchTeamClubId(String(matchData.team_id), clubIdCache) : null);

        if (clubId) {
          try {
            dbUpdates.location = await encryptValue({
              plaintext: updates.location,
              clubId,
              teamId: String(matchData?.team_id || '')
            });
          } catch {
            // if fail, use original
          }
        }
      }

      // Ensure club_id is set if missing or if team_id changed
      const matchData = await getMatchMeta();
      if (!matchData?.club_id && matchData?.team_id) {
        // Current match has no club_id, try to fill it
        const resolvedClubId = await fetchTeamClubId(String(matchData.team_id), clubIdCache);
        if (resolvedClubId) {
          dbUpdates.club_id = resolvedClubId;
        }
      } else if (updates.team_id && updates.team_id !== matchData?.team_id) {
        // Team changed, update club_id
        const resolvedClubId = await fetchTeamClubId(String(updates.team_id), clubIdCache);
        if (resolvedClubId) {
          dbUpdates.club_id = resolvedClubId;
        }
      }

      const savedMatch = await updateMatchApi(id, dbUpdates as Record<string, unknown>);
      const { expected_updated_at: _expectedRevision, ...savedUpdates } = updates;
      updates = { ...savedUpdates, updated_at: (savedMatch as Match | null)?.updated_at };
      if ('trim_substitution_schedule_from' in updates && savedMatch && typeof savedMatch === 'object') {
        const { trim_substitution_schedule_from: _, ...rest } = updates;
        updates = { ...rest, substitution_schedule: (savedMatch as Match).substitution_schedule, preparation_settings: (savedMatch as Match).preparation_settings };
      }
      if ('player_participation' in updates) {
        assertParticipationSaved(savedMatch, updates.player_participation ?? null);
      }

      if (savedMatch && typeof savedMatch === 'object' && 'substitution_schedule' in savedMatch
        && (savedMatch as Record<string, unknown>).substitution_schedule === null) {
        updates = { ...updates, substitution_schedule: null };
        if (matchData?.substitution_schedule && Object.keys(matchData.substitution_schedule).length) {
          window.dispatchEvent(new CustomEvent('schedule-invalidated', { detail: { matchId: id } }));
        }
      }
      set((state) => ({
        ...(() => {
          const nextMatches = state.matches.map(match => {
            if (match.id !== id) return match;

            const hasSubUpdate = sanitizedSubstitutionsForUpdate !== undefined;
            const updatedSubsPerQuarter = hasSubUpdate
              ? sanitizedSubstitutionsForUpdate ?? null
              : match.substitutions_per_quarter ?? null;

            const metaFromUpdate = typeof nextMatchTypeMeta !== 'undefined'
              ? nextMatchTypeMeta
              : match.match_type_meta ?? null;

            const nextMatch: Match = {
              ...match,
              ...updates,
              substitutions_per_quarter: updatedSubsPerQuarter,
              match_type_meta: metaFromUpdate,
            };

            if (updates.formation_type) {
              nextMatch.formation_type = updates.formation_type;
            }

            return nextMatch;
          });

          const updatedMatch = nextMatches.find(m => m.id === id) || null;
          const isCurrent = state.currentMatchId === id;

          const baseConfig: MatchConfig = {
            ...state.matchConfig,
            formation_key:
              typeof updates.formation_key === 'string'
                ? updates.formation_key
                : state.matchConfig.formation_key,
            formation_type:
              updates.formation_type
                ? toInternalFormationType(updates.formation_type)
                : state.matchConfig.formation_type,
          };

          const shouldRecalculateCurrentMatchConfig =
            isCurrent && updatedMatch && (
              typeof nextMatchTypeMeta !== 'undefined' ||
              'field' in updates ||
              'formation_key' in updates ||
              'formation_type' in updates ||
              'substitutions_per_quarter' in updates
            );

          const nextConfig =
            shouldRecalculateCurrentMatchConfig
              ? deriveMatchConfigFromMeta(
                baseConfig,
                updatedMatch.match_type_meta ?? null,
                updatedMatch.substitutions_per_quarter ?? null
              )
              : state.matchConfig;

          return {
            matches: nextMatches,
            matchConfig: nextConfig
          };
        })()
      }));
    } catch (error) {
      throw error;
    }
  },
  deleteMatch: async (id: string) => {
    try {
      await deleteMatchApi(id);

      set((state) => ({
        matches: state.matches.filter(match => match.id !== id),
        currentMatchId: state.currentMatchId === id ? null : state.currentMatchId,
        editingMatchId: state.editingMatchId === id ? null : state.editingMatchId,
        editingMatchLineup: state.editingMatchId === id ? null : state.editingMatchLineup,
        editingMatchSubstitutions: state.editingMatchId === id ? null : state.editingMatchSubstitutions
      }));
    } catch (error) {
      throw error;
    }
  },
  setCurrentMatch: async (id: string | null) => {
    if (!id) {
      set({
        currentMatchId: null,
        editingMatchId: null,
        editingMatchLineup: null,
        editingMatchSubstitutions: null
      });
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('currentMatchId');
      }
      return;
    }

    const existing = get().matches.find(m => m.id === id);

    if (existing) {
      const existingLineupIsReady = !hasEncryptedMatchPlayerNames(existing);
      set({
        currentMatchId: id,
        editingMatchId: id,
        editingMatchLineup: existingLineupIsReady ? existing.lineup : null,
        editingMatchSubstitutions: existing.substitutions || []
      });
    } else {
      set({ currentMatchId: id });
    }

    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('currentMatchId', id);
    }

    try {
      const matchPayload = await getMatchApi(id);
      if (!matchPayload || typeof matchPayload !== 'object') throw new Error('Match not found');
      const data = matchPayload as MatchApiRecord;

      const clubIdCache: ClubIdCache = new Map();
      const clubId = await resolveMatchClubId(data, clubIdCache);
      data.club_id = clubId ?? data.club_id ?? null;

      // Decrypt if necessary
      if (clubId) {
        if (data.knhb_match_id) {
          data.knhb_match_id =
            (await decryptIfEncrypted({ value: asNullableString(data.knhb_match_id), clubId })) ?? data.knhb_match_id;
        }
        if (data.home_team) {
          data.home_team = (await decryptIfEncrypted({ value: asNullableString(data.home_team), clubId })) ?? data.home_team;
        }
        if (data.away_team) {
          data.away_team = (await decryptIfEncrypted({ value: asNullableString(data.away_team), clubId })) ?? data.away_team;
        }
        if (data.location) {
          data.location = (await decryptIfEncrypted({ value: asNullableString(data.location), clubId })) ?? data.location;
        }
        if (data.lineup) {
          data.lineup = await maybeDecryptPlayerRefs(data.lineup, clubId) as MatchApiRecord;
        }
        if (data.reserve_players) {
          data.reserve_players = await maybeDecryptPlayerRefs(data.reserve_players, clubId) as MatchApiRecord[];
        }
        if (data.substitution_schedule) {
          data.substitution_schedule = await maybeDecryptPlayerRefs(data.substitution_schedule, clubId) as MatchApiRecord;
        }
        if (data.substitutions) {
          data.substitutions = await maybeDecryptPlayerRefs(data.substitutions, clubId) as MatchApiRecord[];
        }
      }


      const normalizedStatus =
        data.status === 'inProgress'
          ? 'in_progress'
          : (data.status as Match['status']) || 'upcoming';

      let matchTypeMeta = data.match_type_meta as MatchTypeMetadata | null ?? await fetchMatchTypeMeta(asString(data.field));
      if (!matchTypeMeta) {
        matchTypeMeta = await fetchMatchTypeMetaFromFormation(asNullableString(data.formation_key));
      }

      const hydratedLineup = await hydrateStoredLineup(data.lineup, clubId);
      const hydratedReservePlayers = await hydrateStoredPlayers(data.reserve_players, clubId);

      const updatedMatch = {
        ...(existing || {}),
        ...data,
        match_key: data.match_key,
        status: normalizedStatus,
        substitution_schedule: data.substitution_schedule ?? null,
        substitutions_per_quarter: asNullableNumber(data.substitutions_per_quarter),
        match_type_meta: matchTypeMeta,
        field: asString(data.field, 'outdoor_full'),
        is_home: asNullableBoolean(data.is_home),
        formation_type: toInternalFormationType((typeof data.formation_type === 'string' ? data.formation_type : null) as 'positions' | 'lines' | null),
        home_team_logo: typeof data.home_team_logo === 'string' ? data.home_team_logo : undefined,
        away_team_logo: typeof data.away_team_logo === 'string' ? data.away_team_logo : undefined,
        lineup: hydratedLineup,
        reserve_players: hydratedReservePlayers,
        standin_players: Array.isArray(data.standin_players) ? data.standin_players.filter((entry): entry is string => typeof entry === 'string') : [],
      } as Match;

      const sanitizedSubsPerQuarter =
        typeof updatedMatch.substitutions_per_quarter === 'number' && Number.isFinite(updatedMatch.substitutions_per_quarter)
          ? Math.max(0, Math.floor(updatedMatch.substitutions_per_quarter))
          : null;

      updatedMatch.substitutions_per_quarter = sanitizedSubsPerQuarter;

      set((state) => {
        const isStillSelected = state.currentMatchId === id;
        return {
          matches: state.matches.some(m => m.id === id)
            ? state.matches.map(m => (m.id === id ? updatedMatch : m))
            : [updatedMatch, ...state.matches],
          ...(isStillSelected
            ? {
                editingMatchId: id,
                editingMatchLineup: updatedMatch.lineup,
                editingMatchSubstitutions: Array.isArray(data.substitutions) ? (data.substitutions as Substitution[]) : [],
                matchConfig: deriveMatchConfigFromMeta(
                  {
                    ...state.matchConfig,
                    formation_key: asString(data.formation_key),
                    formation_type: toInternalFormationType((typeof data.formation_type === 'string' ? data.formation_type : null) as 'positions' | 'lines' | null)
                  },
                  matchTypeMeta,
                  sanitizedSubsPerQuarter
                )
              }
            : {})
        };
      });
    } catch (error) {
      throw error;
    }
  },
  assignPlayerToPosition: async (matchId: string, position: Position, player: Player) => {
    if (matchId !== get().currentMatchId) {
      throw new Error('Cannot assign player to a match that is not currently selected');
    }

      const match = get().matches.find(m => m.id === matchId);
      if (!match) throw new Error('Match not found');

      try {
        const playerWithPriority = withAssignedPriority(player, position);
        await get().updateMatch(matchId, {
          lineup: { ...match.lineup, [position]: playerWithPriority }
        });


        set((state) => ({
        editingMatchLineup: {
          ...state.editingMatchLineup || match.lineup,
          [position]: playerWithPriority
        }
      }));
    } catch (error) {
      throw error;
    }
  },
  removePlayerFromPosition: async (matchId: string, position: Position) => {
    if (matchId !== get().currentMatchId) {
      throw new Error('Cannot remove player from a match that is not currently selected');
    }

    const match = get().matches.find(m => m.id === matchId);
    if (!match) throw new Error('Match not found');

    try {
      set((state) => ({
        editingMatchLineup: {
          ...state.editingMatchLineup || match.lineup,
          [position]: null
        }
      }));

      const updatedLineup = match.lineup ? { ...match.lineup, [position]: null } : { [position]: null };
      const updatedReservePlayers = match.reserve_players.filter(p => p.id !== (match.lineup?.[position] || {}).id);
      await get().updateMatch(matchId, {
        lineup: updatedLineup,
        reserve_players: updatedReservePlayers
      });
    } catch (error) {
      throw error;
    }
  },
  addSubstitution: async (matchId: string, substitution: Omit<Substitution, 'id'>, occupation) => {
    if (matchId !== get().currentMatchId) {
      throw new Error('Cannot add substitution to a match that is not currently selected');
    }

    const match = get().matches.find(m => m.id === matchId);
    if (!match) throw new Error('Match not found');
    const currentLineup = occupation?.lineup || get().editingMatchLineup || match.lineup;
    const currentSubstitutions = get().editingMatchSubstitutions || match.substitutions || [];
    const nextOccupation = applyLiveSubstitution({
      lineup: currentLineup,
      reserves: occupation?.reserves || match.reserve_players || [],
      position: substitution.position,
      playerOut: substitution.playerOut,
      playerIn: substitution.playerIn,
    });
    const sanitized: Omit<Substitution, 'id'> = {
      ...substitution,
      position: nextOccupation.position,
      playerOut: sanitizePlayerForSchedule(substitution.playerOut),
      playerIn: sanitizePlayerForSchedule(substitution.playerIn)
    };

    const newSubstitution = { ...sanitized, id: crypto.randomUUID() };
    const updatedSubstitutions = [...currentSubstitutions, newSubstitution];
    await get().updateMatch(matchId, {
      lineup: nextOccupation.lineup,
      reserve_players: nextOccupation.reserves,
      substitutions: updatedSubstitutions,
    });
    set({
      editingMatchLineup: nextOccupation.lineup,
      editingMatchSubstitutions: updatedSubstitutions,
    });
  },
  updateSubstitution: async (matchId: string, id: string, updates: Partial<Substitution>) => {
    if (matchId !== get().currentMatchId) {
      throw new Error('Cannot update substitution for a match that is not currently selected');
    }

    const match = get().matches.find(m => m.id === matchId);
    if (!match) return;

    const sanitizedUpdates: Partial<Substitution> = { ...updates };
    if (updates.playerOut) {
      sanitizedUpdates.playerOut = sanitizePlayerForSchedule(updates.playerOut);
    }
    if (updates.playerIn) {
      sanitizedUpdates.playerIn = sanitizePlayerForSchedule(updates.playerIn);
    }

    set(state => ({
      editingMatchSubstitutions: (state.editingMatchSubstitutions || match.substitutions).map(sub =>
        sub.id === id ? { ...sub, ...sanitizedUpdates } : sub
      )
    }));

    const updatedSubstitutions = match.substitutions.map(sub =>
      sub.id === id ? { ...sub, ...sanitizedUpdates } : sub
    );

    await get().updateMatch(matchId, {
      substitutions: updatedSubstitutions
    });
  },
  deleteSubstitution: async (matchId: string, id: string) => {
    const match = get().matches.find(m => m.id === matchId);
    if (!match) return;

    await get().updateMatch(matchId, {
      substitutions: match.substitutions.filter(sub => sub.id !== id)
    });
  },
  getNextSubstitutionTime: (matchId: string) => {
    const match = get().matches.find(m => m.id === matchId);
    if (!match) return null;

    const lastSubstitution = (get().editingMatchSubstitutions || match.substitutions)[
      (get().editingMatchSubstitutions || match.substitutions).length - 1
    ];
    if (!lastSubstitution) return null;

    return lastSubstitution.time + 5;
  },
  updateMatchTime: async (matchId: string, time: number) => {
    await get().updateMatch(matchId, { match_time: time });
  },
  updateMatchStatus: async (matchId: string, status: Match['status']) => {
    try {
      const dbStatus = status === 'in_progress' ? 'inProgress' : status;
      await updateMatchApi(matchId, { status: dbStatus, updated_at: new Date().toISOString() });

      set((state) => ({
        matches: state.matches.map(match =>
          match.id === matchId ? { ...match, status } : match
        )
      }));

      // Persisted player statistics are rebuilt by the backend batch from
      // match history. Closing/reopening a match must never increment caches.
    } catch (error) {
      throw error;
    }
  },
  updateMatchQuarter: async (matchId: string, quarter: number) => {
    await get().updateMatch(matchId, { current_quarter: quarter });
  },
  updateMatchConfig: (config: Partial<MatchConfig>) => {
    set((state) => ({
      matchConfig: { ...state.matchConfig, ...config }
    }));
  },
  getCurrentMatch: (): Match | null => {
    const id = get().currentMatchId;
    if (!id) return null;
    const match = get().matches.find(m => m.id === id);
    if (!match) return null;

    const currentMatch: Match = {
      ...match,
      status: match.status,
      lineup: get().editingMatchId === id ? get().editingMatchLineup || match.lineup : match.lineup,
      substitutions: get().editingMatchId === id ? get().editingMatchSubstitutions || match.substitutions : match.substitutions
    };

    return currentMatch;
  },
  getPlayerStatsForMatch: (matchId: string, playerId: string): { playingTime: number, playingTimePercentage: number } => {
    const match = get().matches.find(m => m.id === matchId);
    if (!match) return { playingTime: 0, playingTimePercentage: 0 };

    const totalTime = match.match_time || 0;
    let playingTime = 0;
    let isOnField = false;
    let lastEventTime = match.match_time || 0;

    const isInStartingLineup = Object.values(get().editingMatchLineup || match.lineup || {})
      .some(player => player?.id === playerId);

    if (isInStartingLineup) {
      isOnField = true;
      lastEventTime = 0;
    }

    const sortedSubstitutions = [...(match.substitutions ?? [])].sort((a, b) => a.time - b.time);

    sortedSubstitutions.forEach(sub => {
      if (sub.playerIn?.id === playerId) {
        isOnField = true;
        lastEventTime = sub.time;
      } else if (sub.playerOut?.id === playerId) {
        isOnField = false;
        playingTime += sub.time - lastEventTime;
      }
    });

    if (isOnField) {
      playingTime += match.match_time - lastEventTime;
    }

    return {
      playingTime,
      playingTimePercentage: totalTime > 0 ? Math.round((playingTime / totalTime) * 100) : 0
    };
  },
  reset: () => {
    set(getInitialState());
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('currentMatchId');
    }
  }
}));
